<script setup>
import { router } from "@inertiajs/vue3";
import { ref } from "vue";

const props = defineProps({
    impersonation: {
        type: Object,
        default: () => ({}),
    },
    showStartButton: {
        type: Boolean,
        default: false,
    },
});

const isImpersonationPending = ref(false);

const leaveImpersonation = () => {
    if (!props.impersonation?.routes?.stop) {
        return;
    }

    isImpersonationPending.value = true;

    router.delete(props.impersonation.routes.stop, {
        preserveScroll: false,
        onFinish: () => {
            isImpersonationPending.value = false;
        },
    });
};

const startImpersonation = () => {
    if (!props.showStartButton || !props.impersonation?.routes?.start) {
        return;
    }

    isImpersonationPending.value = true;

    router.post(
        props.impersonation.routes.start,
        {},
        {
            preserveScroll: true,
            onFinish: () => {
                isImpersonationPending.value = false;
                window.location.href = "/dashboard/links";
            },
        }
    );
};
</script>

<template>
    <div
        v-if="
            impersonation?.isImpersonating ||
            (showStartButton && impersonation?.canImpersonateTarget)
        "
        class="impersonation-bar"
    >
        <span class="impersonation-bar__message">
            <template v-if="impersonation?.isImpersonating">
                You are logged in as
                <strong>{{
                    impersonation.impersonator?.name ||
                    impersonation.impersonator?.username
                }}</strong>
                managing
                <strong>{{
                    impersonation.impersonated?.username || "this user"
                }}</strong
                >'s account.
            </template>
            <template
                v-else-if="
                    showStartButton && impersonation?.canImpersonateTarget
                "
            >
                You can impersonate this profile.
            </template>
        </span>
        <div class="impersonation-bar__actions">
            <button
                v-if="
                    showStartButton &&
                    impersonation?.canImpersonateTarget &&
                    !impersonation?.isImpersonating
                "
                type="button"
                class="amo-btn amo-btn--secondary amo-btn--sm"
                :disabled="isImpersonationPending"
                @click="startImpersonation"
            >
                Login as {{ impersonation.targetLabel || "this user" }}
            </button>
            <button
                v-if="impersonation?.isImpersonating"
                type="button"
                class="amo-btn amo-btn--danger amo-btn--sm"
                :disabled="isImpersonationPending"
                @click="leaveImpersonation"
            >
                Stop managing
            </button>
        </div>
    </div>
</template>

<style>
.impersonation-bar {
    position: fixed;
    top: 0;
    z-index: 150;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 1rem;
    width: 100%;
    background: var(--black);
    color: #f8fafc;
}

.impersonation-bar__message {
    flex: 1 1 220px;
    font-size: 0.95rem;
}

.impersonation-bar__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: flex-end;
}

.impersonation-bar__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.55rem 1.05rem;
    border-radius: 0.5rem;
    border: 1px solid rgba(248, 250, 252, 0.7);
    background: transparent;
    color: inherit;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
}

.impersonation-bar__button--primary {
    background: #38bdf8;
    border-color: #38bdf8;
    color: #0f172a;
}

.impersonation-bar__button:hover:not([disabled]) {
    background: rgba(148, 163, 184, 0.25);
}

.impersonation-bar__button--primary:hover:not([disabled]) {
    background: #0ea5e9;
    color: #fff;
}

.impersonation-bar__button[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
}

html:has(.impersonation-bar) {
    padding-top: 3rem;
}
</style>
